/* istanbul ignore file - Legacy grid configuration, not actively used in current implementation */
import {
    BaseDealSummaryFieldsContainer,
    CompactBaseDealSummaryFieldsContainer,
    BaseDealSummarySectionContainer,
    CompactBaseDealSummarySectionContainer,
    Sections,
    SummaryField,
    CompactSummaryField
} from './BaseDealSummary.style';
import React from 'react';
import { useSelector } from 'react-redux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { useBaseDealBreakpoint } from '../../../utils/useBaseDealBreakpoint';

export type SectionField = {
    component: React.ReactElement;
    desktopSpan?: number;
    tabletSpan?: number;
    mobileSpan?: number;
    fieldSpan?: number;
    condition?: (type: OfferType) => boolean;
    isFlexWrapper?: boolean;
};

const paymentGridOpenConfig = (): ColumnConfigProps => {
    return {
        desktop: 3,
        tablet: 3,
        mobile: 2
    };
};

export type ColumnConfigProps = {
    desktop: number;
    tablet: number;
    mobile: number;
};

export type ColumnConfig = ColumnConfigProps & {
    paymentGridOpen: ColumnConfigProps;
};

export const summaryLeftColumnConfig: Record<OfferType, ColumnConfig> = {
    [LEASE]: {
        desktop: 3,
        tablet: 3,
        mobile: 2,
        paymentGridOpen: paymentGridOpenConfig()
    },
    [FINANCE]: {
        desktop: 2,
        tablet: 2,
        mobile: 2,
        paymentGridOpen: paymentGridOpenConfig()
    },
    [CASH]: {
        desktop: 5,
        tablet: 3,
        mobile: 2,
        paymentGridOpen: paymentGridOpenConfig()
    },
    [BALLOON]: {
        desktop: 2,
        tablet: 2,
        mobile: 2,
        paymentGridOpen: paymentGridOpenConfig()
    }
};
export const summaryRightColumnConfig: Record<OfferType, ColumnConfig> = {
    [LEASE]: {
        desktop: 2,
        tablet: 3,
        mobile: 2,
        paymentGridOpen: paymentGridOpenConfig()
    },
    [FINANCE]: {
        desktop: 4,
        tablet: 3,
        mobile: 2,
        paymentGridOpen: paymentGridOpenConfig()
    },
    [CASH]: {
        desktop: 2,
        tablet: 3,
        mobile: 2,
        paymentGridOpen: paymentGridOpenConfig()
    },
    [BALLOON]: {
        desktop: 4,
        tablet: 3,
        mobile: 2,
        paymentGridOpen: paymentGridOpenConfig()
    }
};

type SummaryGridProps = {
    leftSectionFields: SectionField[];
    rightSectionFields?: SectionField[];
    columnConfig?: ColumnConfigProps;
    isCompactUI?: boolean;
};

export const SummaryGrid = ({ leftSectionFields, rightSectionFields = [], columnConfig, isCompactUI }: SummaryGridProps) => {
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const currentOfferType: OfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const paymentGridEnabledToggle = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const flexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);

    const isPaymentGridEnabled =
        (isFlexBuyPayment ? flexBuyPaymentGridEnabled : paymentGridEnabledToggle) && isDealCentralExperience;

    const breakpoints = useBaseDealBreakpoint({ summaryTab: true, isPaymentGridEnabled });
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const isSingleColumn = !rightSectionFields.length;

    const getColumnConfig = (summaryColConfig: Record<OfferType, ColumnConfig>, isPaymentGridEnabled: boolean) => {
        const summaryColumnConfig = summaryColConfig[currentOfferType];
        return isPaymentGridEnabled ? summaryColumnConfig.paymentGridOpen : summaryColumnConfig;
    };

    const summaryLeftSectionColumnConfig = getColumnConfig(summaryLeftColumnConfig, isPaymentGridEnabled);

    const summaryRightSectionColumnConfig = getColumnConfig(summaryRightColumnConfig, isPaymentGridEnabled);

    const Field = isCompactUI ? CompactSummaryField : SummaryField;
    const SectionContainer = isCompactUI ? CompactBaseDealSummarySectionContainer : BaseDealSummarySectionContainer;
    const FieldsContainer = isCompactUI ? CompactBaseDealSummaryFieldsContainer : BaseDealSummaryFieldsContainer;

    const renderField = (field: SectionField, index: number) => {
        const desktopSpan = field.desktopSpan || field.fieldSpan || 1;
        const tabletSpan = field.tabletSpan || field.fieldSpan || 1;
        const mobileSpan = field.mobileSpan || field.fieldSpan || 1;

        return (
            (!field.condition || field.condition(currentOfferType)) && (
                <Field
                    lg={desktopSpan}
                    md={tabletSpan}
                    sm={mobileSpan}
                    key={index}
                    breakpoints={breakpoints}
                    offerType={currentOfferType}
                    isFlexWrapper={field.isFlexWrapper}
                >
                    {field.component}
                </Field>
            )
        );
    };

    return (
        <SectionContainer
            isPaymentGridEnabled={isPaymentGridEnabled}
            data-testid="base-deal-summary-section-container"
            breakpoints={breakpoints}
            offerType={currentOfferType}
            isDealCentralExperience={isDealCentralExperience}
            isDealCentralMultiScenario={isDealCentralMultiScenario}
        >
            <FieldsContainer
                section={Sections.Left}
                offerType={currentOfferType}
                lg={(columnConfig || summaryLeftSectionColumnConfig).desktop}
                md={(columnConfig || summaryLeftSectionColumnConfig).tablet}
                sm={(columnConfig || summaryLeftSectionColumnConfig).mobile}
                breakpoints={breakpoints}
                isSingleColumn={isSingleColumn}
                isPaymentGridEnabled={isPaymentGridEnabled}
                isDealCentralExperience={isDealCentralExperience}
                data-testid="base-deal-summary-left-section"
            >
                {leftSectionFields.map((field, index) => renderField(field, index))}
            </FieldsContainer>

            {!isSingleColumn && (
                <FieldsContainer
                    section={Sections.Right}
                    offerType={currentOfferType}
                    lg={(columnConfig || summaryRightSectionColumnConfig).desktop}
                    md={(columnConfig || summaryRightSectionColumnConfig).tablet}
                    sm={(columnConfig || summaryRightSectionColumnConfig).mobile}
                    breakpoints={breakpoints}
                    isPaymentGridEnabled={isPaymentGridEnabled}
                    isDealCentralExperience={isDealCentralExperience}
                    data-testid="base-deal-summary-right-section"
                >
                    {rightSectionFields.map((field, index) => renderField(field, index))}
                </FieldsContainer>
            )}
        </SectionContainer>
    );
};
